/**
 * MODULE: domain/sales-intelligence/frontend/components/QuickSetupModal.tsx
 * PURPOSE: Modal for quick setup of seller context (JIT context collection).
 * DEPENDENCIES: flowbite-react, useSaveQuickSetup, types/seller-context
 * PUBLIC_API: <QuickSetupModal isOpen onComplete onSkip />
 * SIDE_EFFECTS: Calls saveQuickSetup (API) on submit.
 * AGENT_SAFETY: 🟢 SAFE
 * FILE_SIZE: 254 / 200 lines (component; slightly over for form fields)
 * Recent: 2026-02-06 Seller context (JIT) – Flowbite placeholders; tests updated.
 */

import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Label, TextInput, Textarea } from 'flowbite-react';
import { HiInformationCircle, HiCheckCircle, HiXCircle } from 'react-icons/hi';
import { useSaveQuickSetup } from '../hooks/useSellerContext';
import type { SellerQuickSetupFormState } from '../types/seller-context';

interface QuickSetupModalProps {
  isOpen: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

const INDUSTRY_OPTIONS = [
  'SaaS',
  'FinTech',
  'E-commerce',
  'Healthcare',
  'Enterprise',
  'Other'
];

const COMPANY_SIZE_OPTIONS = [
  { label: '1-50', value: 'small' as const },
  { label: '51-500', value: 'medium' as const },
  { label: '501-5000', value: 'large' as const },
  { label: '5000+', value: 'enterprise' as const }
];

export const QuickSetupModal: React.FC<QuickSetupModalProps> = ({
  isOpen,
  onComplete,
  onSkip
}) => {
  const [formData, setFormData] = useState<SellerQuickSetupFormState>({
    companyName: '',
    valueProp: '',
    targetIndustries: [],
    idealSize: 'medium',
    painPoints: [],
    integrations: [],
    competitors: []
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saveQuickSetup = useSaveQuickSetup();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!formData.companyName.trim()) {
      setError('Company name is required');
      return;
    }
    if (!formData.valueProp.trim() || formData.valueProp.trim().length < 10) {
      setError('Value proposition must be at least 10 characters');
      return;
    }
    if (formData.targetIndustries.length === 0) {
      setError('Select at least one target industry');
      return;
    }
    if (!formData.idealSize) {
      setError('Select ideal company size');
      return;
    }
    try {
      await saveQuickSetup.mutateAsync(formData);
      onComplete();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save setup');
    }
  };

  const handleIndustryToggle = (industry: string) => {
    setFormData((prev) => ({
      ...prev,
      targetIndustries: prev.targetIndustries.includes(industry)
        ? prev.targetIndustries.filter((i) => i !== industry)
        : [...prev.targetIndustries, industry]
    }));
  };

  return (
    <Modal show={isOpen} onClose={onSkip} size="2xl">
      <ModalHeader>
        <div className="flex items-center gap-2">
          <HiInformationCircle className="h-6 w-6 text-blue-600" />
          <span>Help Us Research Better</span>
        </div>
      </ModalHeader>

      <ModalBody>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Tell us about your company and ideal customers to get 10x more relevant insights. Takes less than 2
            minutes.
          </p>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <HiXCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" id="quick-setup-form">
            <div>
              <Label htmlFor="companyName">Your Company Name *</Label>
              <TextInput
                id="companyName"
                type="text"
                placeholder="e.g., Acme Corp"
                value={formData.companyName}
                onChange={(e) => setFormData((prev) => ({ ...prev, companyName: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label htmlFor="valueProp">What problem do you solve? *</Label>
              <Textarea
                id="valueProp"
                placeholder="e.g., We help companies automate their sales research with AI"
                rows={3}
                value={formData.valueProp}
                onChange={(e) => setFormData((prev) => ({ ...prev, valueProp: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label>Target Industries *</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {INDUSTRY_OPTIONS.map((industry) => (
                  <button
                    key={industry}
                    type="button"
                    onClick={() => handleIndustryToggle(industry)}
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                      formData.targetIndustries.includes(industry)
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {industry}
                    {formData.targetIndustries.includes(industry) && (
                      <HiCheckCircle className="h-4 w-4" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="idealSize">Ideal Customer Size (employees) *</Label>
              <select
                id="idealSize"
                value={formData.idealSize}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    idealSize: e.target.value as SellerQuickSetupFormState['idealSize'],
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select size range</option>
                {COMPANY_SIZE_OPTIONS.map((size) => (
                  <option key={size.value} value={size.value}>
                    {size.label} employees
                  </option>
                ))}
              </select>
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-sm text-blue-600 hover:underline"
              >
                {showAdvanced ? '▼ Hide' : '▶ Show'} advanced options (optional)
              </button>

              {showAdvanced && (
                <div className="mt-4 space-y-4 pl-4 border-l-2 border-gray-200">
                  <div>
                    <Label htmlFor="painPoints">Common Pain Points (comma-separated)</Label>
                    <Textarea
                      id="painPoints"
                      placeholder="e.g., Manual research takes too long, Hard to find decision makers"
                      rows={2}
                      value={Array.isArray(formData.painPoints) ? formData.painPoints.join(', ') : (formData.painPoints ?? '')}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          painPoints: e.target.value.split(',').map((p) => p.trim()).filter(Boolean)
                        }))
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="competitors">Main Competitors (comma-separated)</Label>
                    <TextInput
                      id="competitors"
                      placeholder="e.g., ZoomInfo, Apollo, LinkedIn Sales Nav"
                      value={(formData.competitors ?? []).join(', ')}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          competitors: e.target.value
                            .split(',')
                            .map((c) => c.trim())
                            .filter(Boolean)
                        }))
                      }
                    />
                    <p className="text-xs text-gray-500 mt-1">Separate with commas</p>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </ModalBody>

      <ModalFooter>
        <div className="flex justify-between w-full">
          <Button color="light" onClick={onSkip} disabled={saveQuickSetup.isPending}>
            Skip for Now
          </Button>
          <div className="flex gap-2">
            <Button color="gray" onClick={onSkip} disabled={saveQuickSetup.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="quick-setup-form"
              disabled={saveQuickSetup.isPending}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white focus:ring-purple-300 dark:focus:ring-purple-800"
            >
              {saveQuickSetup.isPending ? 'Saving...' : 'Save & Start Research'}
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};
